import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { DynamicIconWrapper, Icons, SliderCheckbox, Tooltip } from '@components/common';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell/AdaptiveTableCell';

import { IConsistencyCheckRan } from '@interfaces/cmModule/consistencyCheck';

import { EEnabledDisabled } from '@enums/common';

import styles from './ConsistencyCheckTable.module.scss';

interface IColumns {
	setSelectedConsistencyCheckRan: (selectedConsistencyCheckRan: IConsistencyCheckRan) => void;
	openAssignTemplateModal: () => void;
	toggleAvailability: (id: string, isEnabled: boolean) => void;
	navigateConsistencyHistory: (id: string) => void;
}

const columnHelper = createColumnHelper<IConsistencyCheckRan>();

export const getColumns = ({
	openAssignTemplateModal,
	setSelectedConsistencyCheckRan,
	toggleAvailability,
	navigateConsistencyHistory,
}: IColumns): ColumnDef<any>[] => [
	columnHelper.accessor('networkElementName', {
		header: 'NE Name',
		cell: ({ getValue }: CellContext<IConsistencyCheckRan, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value}/>;
		},
	}),
	columnHelper.accessor('goldenTemplateName', {
		header: () => <>Assigned Reference<br/>Template</>,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckRan, any>) => {
			const { goldenTemplateName } = original;

			const textClassName = goldenTemplateName ? undefined : styles.linkText;

			const handleAssignTemplate = () => {
				setSelectedConsistencyCheckRan(original);
				openAssignTemplateModal();
			};

			return (
				<div className={styles.actionCell}>
					<span
						className={textClassName}
						onClick={!goldenTemplateName ? handleAssignTemplate : undefined}
					>{goldenTemplateName || 'Assign'}</span>
					<DynamicIconWrapper
						icon={goldenTemplateName ? Icons.editIcon : Icons.link}
						onClick={handleAssignTemplate}
						tooltipLabel={goldenTemplateName ? 'Reassign' : 'Assign'}
					/>
				</div>
			);
		},
	}),
	columnHelper.accessor('lastCheckTimeText', {
		header: 'Last Checked',
		cell: ({ getValue }: CellContext<IConsistencyCheckRan, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value || 'N/A'}/>;
		},
	}),
	columnHelper.accessor('consistencyStatus', {
		header: () => <>Consistency<br/>Status</>,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckRan, any>) => {
			const { title, color } = original?.consistencyStatus || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('id', {
		header: () => <>Consistency<br/>History</>,
		cell: ({ getValue }: CellContext<IConsistencyCheckRan, any>) => {
			const goldenTemplateId = getValue();

			const handleNavigateConsistencyHistory = () => {
				if (!goldenTemplateId) {
					return;
				}

				navigateConsistencyHistory(goldenTemplateId);
			};

			return (
				goldenTemplateId
					? (
						<div className={styles.actionCell}>
							<span
								className={styles.linkText}
								onClick={goldenTemplateId ? handleNavigateConsistencyHistory : undefined}
							>View</span>
							<DynamicIconWrapper
								icon={Icons.viewIcon}
								onClick={handleNavigateConsistencyHistory}
							/>
						</div>
					) : <AdaptiveTableCell text='-' />
			);
		},
	}),
	columnHelper.accessor('monitoringStatus', {
		header: () => <>MONITORING<br/>STATUS</>,
		cell: ({ row: { original } }: CellContext<IConsistencyCheckRan, any>) => {
			const {
				monitoringStatus: { name },
			} = original || {};

			const isEnabled = name === EEnabledDisabled.enabled;

			const handleToggle = () => {
				if (name) {
					toggleAvailability(original.id, isEnabled);
				}
			};

			return (
				<div className={styles.actionCell}>
					{name
						? (
							<Tooltip content={isEnabled ? 'Disable Monitoring' : 'Enable Monitoring'}>
								<div>
									<SliderCheckbox
										checked={isEnabled}
										id='monitoringStatus'
										onChange={handleToggle}
									/>
								</div>
							</Tooltip>

						)
						: (<AdaptiveTableCell text='N/A'/>)
					}
				</div>
			);
		},
	}),
];
